import type {
  Configuration,
  EndSessionRequest,
  IPublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? '';
const clientId = import.meta.env.VITE_ENTRA_SPA_CLIENT_ID ?? '';
const redirectUri =
  import.meta.env.VITE_ENTRA_REDIRECT_URI ??
  (typeof window === 'undefined' ? 'http://localhost' : window.location.origin);

/**
 * Survives MSAL's sessionStorage cache clear on logout. When set, the next
 * `loginRedirect` uses `prompt: 'select_account'` so a leftover Microsoft SSO
 * cookie (Outlook/Teams in another tab) cannot silently sign the same user back in.
 */
export const SELECT_ACCOUNT_ON_NEXT_LOGIN_KEY = 'ucp.auth.promptSelectAccount';

/**
 * MSAL configuration for the public SPA. The SPA holds no secret; it acquires
 * delegated access tokens for the confidential API (scopes ucp.send /
 * ucp.approve / ucp.admin). See pre-flight item 07 (security, identity & access).
 */
export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const apiScopes = (import.meta.env.VITE_ENTRA_API_SCOPES ?? '')
  .split(' ')
  .map((scope) => scope.trim())
  .filter((scope) => scope.length > 0);

export const loginRequest: RedirectRequest = {
  scopes: apiScopes.length > 0 ? apiScopes : ['openid', 'profile'],
};

function markSelectAccountOnNextLogin(): void {
  try {
    sessionStorage.setItem(SELECT_ACCOUNT_ON_NEXT_LOGIN_KEY, '1');
  } catch {
    // Private-mode quota / disabled storage — Sign in still works, just may SSO.
  }
}

/**
 * Login request for the landing / re-auth buttons. After an explicit Sign out the
 * next click asks Entra for an account picker instead of a silent SSO bounce.
 */
export function consumeLoginRequest(): RedirectRequest {
  let selectAccount = false;
  try {
    selectAccount = sessionStorage.getItem(SELECT_ACCOUNT_ON_NEXT_LOGIN_KEY) === '1';
    if (selectAccount) {
      sessionStorage.removeItem(SELECT_ACCOUNT_ON_NEXT_LOGIN_KEY);
    }
  } catch {
    selectAccount = false;
  }

  if (!selectAccount) {
    return loginRequest;
  }

  return { ...loginRequest, prompt: 'select_account' };
}

/**
 * Entra end-session request. Passing the account supplies `logout_hint` /
 * `id_token_hint` so Entra honours `post_logout_redirect_uri` and returns to the
 * SPA instead of the generic "close all browser windows" page.
 */
export function buildLogoutRequest(instance: IPublicClientApplication): EndSessionRequest {
  markSelectAccountOnNextLogin();
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  return {
    ...(account ? { account } : {}),
    postLogoutRedirectUri: redirectUri,
  };
}
